import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import type { ServiceStoreType } from "../../../types/ServiceStoreTypes";
import { findOrThrow, loadErrorMessage } from "shared/loadOrError";
import {
  nextSort,
  sortRows,
  textValue,
  type SortDir,
  type SortValue,
} from "components/TableSort/tableSort";
import { THROW_ON_ERROR } from "shared/dialogSubmit";
import { isSessionEnded } from "stores/sessionGuard";

/** A partner record as the `partners` service serialises it. */
export interface Partner {
  id: string;
  name: string;
  type: string;
  status: string;
  website?: string | null;
  notes?: string | null;
}

export interface PartnerFormData {
  name?: string;
  type?: string;
  status?: string;
  website?: string | null;
  notes?: string | null;
}

export const PARTNER_TYPES = ["sales", "integration", "referral", "technology", "reseller", "other"];

export type PartnerSortKey = "name" | "type" | "status" | "website";

/** All columns are text — every sort starts A→Z. */
const DESC_FIRST: ReadonlySet<PartnerSortKey> = new Set();

/**
 * Status filter. The service defaults `status` to "active" but stores a free
 * string, and the table renders it binary (active vs. everything else) — so
 * "inactive" here means any status other than "active".
 */
export type PartnerStatusFilter = "all" | "active" | "inactive";

/**
 * View store for the Meeting Intelligence "Partners" admin list. Plain MobX class
 * (no ViewStore base). Backed by the registered `partners` ServiceStore; the list
 * is a computed view over its entity cache, only the form state lives here.
 *
 * Registered as `partnersAdminStore` to avoid colliding with the `partners`
 * ServiceStore's own `rootStore.partnersStore` property (see
 * [[web-servicestore-access-collision]]); the ServiceStore is resolved via
 * serviceStoreManager.getStore("partners").
 */
class PartnersStore {
  rootStore: RootStore;

  loading = false;
  error: string | null = null;

  // Header filters
  searchQuery = "";
  /** "" = all types; a PARTNER_TYPES value narrows to that type. */
  typeFilter = "";
  statusFilter: PartnerStatusFilter = "all";

  // Column sort (client-side over the filtered set; see tableSort.ts)
  sortKey: PartnerSortKey = "name";
  sortDir: SortDir = "asc";

  editingId: string | null = null;
  formData: PartnerFormData = {};
  saving = false;
  formError: string | null = null;
  /** A refused Remove on the list, in the API's words — shown above the table. */
  actionError: string | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  private get service(): ServiceStoreType | null {
    return (
      ((this.rootStore.serviceStoreManager?.getStore("partners") ?? this.rootStore.partnersStore) as unknown as ServiceStoreType | null) ?? null
    );
  }

  get partners(): Partner[] {
    return (this.service?.entities ?? []) as unknown as Partner[];
  }

  get filteredPartners(): Partner[] {
    let result = this.partners;

    if (this.typeFilter) {
      result = result.filter((p) => p.type === this.typeFilter);
    }

    if (this.statusFilter === "active") {
      result = result.filter((p) => p.status === "active");
    } else if (this.statusFilter === "inactive") {
      result = result.filter((p) => p.status !== "active");
    }

    const q = this.searchQuery.toLowerCase().trim();
    if (q) {
      // Search covers every column the table shows: name, type, status, website.
      result = result.filter(
        (p) =>
          p.name?.toLowerCase().includes(q) ||
          p.type?.toLowerCase().includes(q) ||
          p.status?.toLowerCase().includes(q) ||
          p.website?.toLowerCase().includes(q)
      );
    }

    return result;
  }

  /**
   * The rows the table renders: filters, then the active column sort. Nulls
   * (e.g. no website) sort last in either direction.
   */
  get sortedPartners(): Partner[] {
    const key = this.sortKey;
    const valueOf = (p: Partner): SortValue => {
      switch (key) {
        case "name":
          return textValue(p.name);
        case "type":
          return textValue(p.type);
        case "status":
          return textValue(p.status);
        case "website":
          return textValue(p.website);
      }
    };
    return sortRows(this.filteredPartners, valueOf, this.sortDir);
  }

  get totalCount(): number {
    return this.partners.length;
  }

  get isEditing(): boolean {
    return this.editingId !== null;
  }

  get isFormValid(): boolean {
    return !!this.formData.name?.trim();
  }

  /** True when a search, type or status filter is set. */
  get hasActiveFilter(): boolean {
    return (
      this.searchQuery.trim().length > 0 ||
      this.typeFilter !== "" ||
      this.statusFilter !== "all"
    );
  }

  setSearchQuery(q: string) {
    this.searchQuery = q;
  }

  setTypeFilter(type: string) {
    this.typeFilter = type;
  }

  setStatusFilter(filter: PartnerStatusFilter) {
    this.statusFilter = filter;
  }

  /** Column-header click: same column toggles direction, a new one starts A→Z. */
  setSort(key: PartnerSortKey) {
    const next = nextSort(this.sortKey, this.sortDir, key, DESC_FIRST);
    this.sortKey = next.key;
    this.sortDir = next.dir;
  }

  async load() {
    this.loading = true;
    this.error = null;
    try {
      // findOrThrow: a swallowed failure rendered "No partners yet" + a 0 tile.
      await findOrThrow(this.service, { query: { $limit: 500, $sort: { name: 1 } } });
    } catch (err: unknown) {
      runInAction(() => {
        if (!isSessionEnded(err)) this.error = loadErrorMessage(err, "Failed to load partners");
      });
      console.error("Failed to load partners:", err);
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  async reload() {
    await this.load();
  }

  startCreate() {
    this.editingId = null;
    this.formError = null;
    this.formData = { name: "", type: "integration", status: "active", website: "", notes: "" };
  }

  startEdit(partner: Partner) {
    this.editingId = partner.id;
    this.formError = null;
    this.formData = {
      name: partner.name,
      type: partner.type,
      status: partner.status,
      website: partner.website ?? "",
      notes: partner.notes ?? "",
    };
  }

  setFormField<K extends keyof PartnerFormData>(field: K, value: PartnerFormData[K]) {
    this.formData = { ...this.formData, [field]: value };
  }

  async savePartner(): Promise<boolean> {
    if (!this.isFormValid) {
      this.formError = "Name is required.";
      return false;
    }
    this.saving = true;
    this.formError = null;
    try {
      const payload: Record<string, unknown> = {
        name: this.formData.name?.trim(),
        type: this.formData.type || "integration",
        status: this.formData.status || "active",
        website: this.formData.website?.trim() || null,
        notes: this.formData.notes?.trim() || null,
      };
      if (this.editingId !== null) {
        await this.service?.patch(this.editingId, payload, THROW_ON_ERROR);
      } else {
        await this.service?.create(payload as never, THROW_ON_ERROR);
      }
      await this.reload();
      runInAction(() => {
        this.editingId = null;
        this.formData = {};
      });
      return true;
    } catch (err: unknown) {
      runInAction(() => {
        if (!isSessionEnded(err)) this.formError = err instanceof Error ? err.message : "Failed to save partner";
      });
      return false;
    } finally {
      runInAction(() => {
        this.saving = false;
      });
    }
  }

  async removePartner(id: string) {
    this.actionError = null;
    try {
      await this.service?.remove(id, THROW_ON_ERROR);
      await this.reload();
    } catch (err) {
      runInAction(() => {
        if (!isSessionEnded(err)) {
          this.actionError =
            err instanceof Error && err.message ? err.message : "Failed to remove the partner";
        }
      });
      console.error("Failed to remove partner:", err);
    }
  }

  clearActionError() {
    this.actionError = null;
  }

  /**
   * Back to the constructed state, so nothing of the person who signed out
   * is left for the next one. Called by RootStore.purgeUserData on sign-out.
   */
  reset() {
    this.loading = false;
    this.error = null;
    this.searchQuery = "";
    this.typeFilter = "";
    this.statusFilter = "all";
    this.sortKey = "name";
    this.sortDir = "asc";
    this.editingId = null;
    this.formData = {};
    this.saving = false;
    this.formError = null;
    this.actionError = null;
  }
}

export default PartnersStore;
